import { Suspense, lazy, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Chess } from 'chess.js'
import {
  acceptGame,
  askGameHint,
  declineGame,
  getGame,
  nudgeGameAgent,
  playGameMove,
  resignGame,
  type GameAgentPick,
  type GameSide,
  type GameView,
} from '../chatv1/transport'
import { onChatv1Frame, onChatv1Open } from '../chatv1/ws'
import { Icon } from '../ui/Icon'
import { Sheet } from '../ui/Sheet'
import { Avatar } from '../chat/Avatar'
import { ChessBoard, type BoardMove } from './ChessBoard'
import { GomokuBoard } from './GomokuBoard'
import { XiangqiBoard } from './XiangqiBoard'
import { afterMove } from './xiangqiGeom'
import { AgentPick } from './AgentPick'
import { GameTalk } from './GameTalk'
import { gameErrorText, moveRows, statusText } from './gameText'
import { kindOf } from './gameKinds'
import './games.css'

/** three.js only for whoever picks 3D — the rest of the app never loads it. */
const ChessBoard3D = lazy(() => import('./ChessBoard3D'))
const GomokuBoard3D = lazy(() => import('./GomokuBoard3D'))
const XiangqiBoard3D = lazy(() => import('./XiangqiBoard3D'))
const VIEW_KEY = 'avg.games.board'

/** The viewer's own choice of board, remembered in this browser (a convenience: unreadable storage means 2D). */
function readView(): '2d' | '3d' {
  try {
    return window.localStorage.getItem(VIEW_KEY) === '3d' ? '3d' : '2d'
  } catch {
    return '2d'
  }
}
function saveView(v: '2d' | '3d'): void {
  try {
    window.localStorage.setItem(VIEW_KEY, v)
  } catch {
    /* a private window: the choice lasts this visit */
  }
}

/**
 * A GAME, FULL SCREEN — owner, 2026-10-19: 西洋棋, 讓兩個使用者可以互相對弈 … 跟他自己的 Agent 來下棋; 外掛 AI 來輔助 …
 * 要讓對弈的對手知道使用者有外掛. Then 五子棋 and 中國象棋: the same screen, each its own board (GomokuBoard,
 * XiangqiBoard), everything else shared.
 *
 * The opponent above the board, the viewer below; whose move it is under it, and the moves made. The game is the
 * service's: it arrives by `game` frames on the chat socket (each the whole game as this person sees it), is read
 * again whenever the screen opens or the socket returns, and a move shows at once and is then confirmed or put back.
 *
 * AI ASSIST: "Hint" asks one of your agents for a move. Before the first ask the screen says what it costs — your
 * opponent sees "AI assist" beside your name for the rest of the game — and from then on that mark is on both
 * boards, drawn from the service's own record.
 */

/** The newer of two readings of one game: frames and fetches can cross. */
function newer(a: GameView | undefined, b: GameView): GameView {
  return a && a.v > b.v ? a : b
}

/** The game as it will be after the viewer's move — shown at once, until the service answers (null: not a move). */
function shown(g: GameView, m: BoardMove): GameView | null {
  const other: GameSide = g.turn === 'w' ? 'b' : 'w'
  if (g.type === 'gomoku') {
    if (g.moves.includes(m.to)) return null
    return { ...g, turn: other, moves: [...g.moves, m.to], last: { from: m.to, to: m.to }, hint: null }
  }
  if (g.type === 'xiangqi') {
    // The app has no copy of xiangqi's rules: a move is shown only when the service listed it.
    if (!(g.legal ?? []).includes(m.from + m.to)) return null
    return {
      ...g,
      fen: afterMove(g.fen, m.from, m.to),
      turn: other,
      moves: [...g.moves, m.from + m.to],
      last: { from: m.from, to: m.to },
      check: false,
      legal: null,
      hint: null,
    }
  }
  try {
    const c = new Chess(g.fen)
    const done = c.move(m)
    return {
      ...g,
      fen: c.fen(),
      turn: c.turn() as GameSide,
      moves: [...g.moves, done.san],
      last: { from: done.from, to: done.to },
      check: c.inCheck(),
      hint: null,
    }
  } catch {
    return null
  }
}

export function GameScreen({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient()
  const key = ['game', id]
  const q = useQuery({
    queryKey: key,
    queryFn: async () => (await getGame(id)).game,
  })
  // The frames are the live path. No timer re-asks (the app's no-heartbeat rule): the game is read again when the
  // socket comes back — the only time a frame can have been missed — and when the window regains focus.
  useEffect(() => {
    const offFrame = onChatv1Frame((f) => {
      if (f.type === 'game' && f.game.id === id) {
        qc.setQueryData<GameView>(['game', id], (old) => newer(old, f.game))
      }
    })
    const offOpen = onChatv1Open(() => void qc.invalidateQueries({ queryKey: ['game', id] }))
    return () => {
      offFrame()
      offOpen()
    }
  }, [id, qc])

  const server = q.data
  // A move shows at once; the service's answer replaces it (or the board goes back, with the reason).
  const [pending, setPending] = useState<GameView | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [armedResign, setArmedResign] = useState(false)
  const [assistOpen, setAssistOpen] = useState(false)
  const [advisor, setAdvisor] = useState<GameAgentPick | null>(null)
  // 2D or 3D (owner, 2026-10-19: 精美的 3D … Three.js); 3D falls back to 2D where WebGL cannot start.
  const [view, setView] = useState<'2d' | '3d'>(readView)
  const [no3d, setNo3d] = useState(false)
  const g = pending && server && pending.v >= server.v ? pending : server

  const play = async (m: BoardMove) => {
    if (!server) return
    setErr(null)
    const next = shown(server, m)
    if (!next) return
    setPending(next)
    try {
      const r = await playGameMove(id, m)
      qc.setQueryData<GameView>(key, (old) => newer(old, r.game))
    } catch (e) {
      setErr(gameErrorText(e))
      void q.refetch()
    } finally {
      setPending(null)
    }
  }

  const act = async (fn: () => Promise<{ game: GameView }>) => {
    setErr(null)
    setBusy(true)
    try {
      const r = await fn()
      qc.setQueryData<GameView>(key, (old) => newer(old, r.game))
      return true
    } catch (e) {
      setErr(gameErrorText(e))
      void q.refetch()
      return false
    } finally {
      setBusy(false)
    }
  }

  const askHint = async (a: GameAgentPick) => {
    setAdvisor(a)
    setAssistOpen(false)
    await act(() => askGameHint(id, a))
  }

  const me: GameSide = g?.you ?? 'w'
  const them: GameSide = me === 'w' ? 'b' : 'w'
  // The moves as a reader writes them (a move just made shows there once the service has named it).
  const written = g?.notation ?? g?.moves
  const rows = useMemo(() => moveRows(written ?? []), [written])
  const myTurn = !!g && g.status === 'active' && g.turn === g.you
  // One description of the board, drawn flat or in 3D.
  const board = g
    ? {
        fen: g.fen,
        orientation: me,
        movable: myTurn && !pending ? me : null,
        last: g.last,
        // A mate is a check too, and the board should say so after the game ends.
        check: g.check || /#$/.test(g.moves[g.moves.length - 1] ?? ''),
        hint: myTurn && g.hint ? { from: g.hint.from, to: g.hint.to } : null,
        onMove: (m: BoardMove) => {
          void play(m)
        },
      }
    : null
  const kind = kindOf(g?.type ?? server?.type)
  // A stone game's board: the stones from the moves, your colour for the stone you are about to place.
  const stones = g
    ? {
        moves: g.moves,
        mine: g.you,
        movable: myTurn && !pending,
        last: g.last?.to ?? null,
        line: g.line,
        hint: myTurn && g.hint ? g.hint.to : null,
        onPlace: (p: string) => {
          void play({ from: p, to: p })
        },
      }
    : null
  // A xiangqi board: where a piece may go is the service's list, never a copy of the rules here.
  const pieces = g
    ? {
        fen: g.fen,
        orientation: me,
        movable: myTurn && !pending ? me : null,
        legal: g.legal ?? [],
        last: g.last,
        check: g.check,
        hint: myTurn && g.hint ? { from: g.hint.from, to: g.hint.to } : null,
        onMove: (m: BoardMove) => {
          void play(m)
        },
      }
    : null
  const flat =
    g?.type === 'gomoku'
      ? stones && <GomokuBoard {...stones} />
      : g?.type === 'xiangqi'
        ? pieces && <XiangqiBoard {...pieces} />
        : board && <ChessBoard {...board} />
  const deep =
    g?.type === 'gomoku'
      ? stones && <GomokuBoard3D {...stones} onUnavailable={() => setNo3d(true)} />
      : g?.type === 'xiangqi'
        ? pieces && <XiangqiBoard3D {...pieces} onUnavailable={() => setNo3d(true)} />
        : board && <ChessBoard3D {...board} onUnavailable={() => setNo3d(true)} />
  const agentStuck =
    !!g && g.status === 'active' && g.turn === them && g.players[them].kind === 'agent' && (!!g.agent_problem || g.agent_thinking)

  return createPortal(
    <div className="gm-screen" role="dialog" aria-label={kind.title}>
      <div className="gm-top">
        <button type="button" className="gm-back" onClick={onClose} aria-label="Close the game">
          <Icon name="chevron_left" size={22} />
          <span>Games</span>
        </button>
        <span className="gm-title">{kind.title}</span>
        <span className="vm-seg gm-viewseg" role="radiogroup" aria-label="Board">
          {(['2d', '3d'] as const).map((v) => (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={view === v}
              className={`vm-seg-btn${view === v ? ' on' : ''}`}
              onClick={() => {
                setView(v)
                saveView(v)
                if (v === '3d') setNo3d(false)
              }}
            >
              {v.toUpperCase()}
            </button>
          ))}
        </span>
      </div>
      {q.isPending && <p className="gh-note gm-note">Loading the game…</p>}
      {q.isError && !g && <p className="gh-note gm-note mch-err">{gameErrorText(q.error)}</p>}
      {g && (
        <div className="gm-body">
          <div className="gm-play">
            <Seat g={g} side={them} />
            {view === '3d' && !no3d ? <Suspense fallback={flat}>{deep}</Suspense> : flat}
            {view === '3d' && no3d && <p className="gh-note">3D is not available on this device — showing the flat board.</p>}
            <Seat g={g} side={me} />
          </div>
          <div className="gm-side">
            <p className={`gm-status${g.status === 'over' ? ' over' : myTurn ? ' mine' : ''}`} role="status">
              {statusText(g)}
            </p>
            {myTurn && (g.hint || g.hint_pending) && (
              <div className="gm-hint">
                <Icon name="auto_awesome" size={16} />
                {g.hint ? (
                  <span>
                    <strong>{g.hint.san}</strong>
                    {g.hint.note ? ` — ${g.hint.note}` : ''}
                    <span className="gm-hint-who"> · {g.advisor?.name ?? 'your agent'}</span>
                  </span>
                ) : (
                  <span>Asking {g.advisor?.name ?? 'your agent'} for a hint…</span>
                )}
              </div>
            )}
            {err && <p className="gh-note mch-err">{err}</p>}
            <div className="gm-actions">
              {/* A friend's invitation: theirs to answer, yours to take back (owner, 2026-10-19: 直接對他的好友送出對弈的邀請). */}
              {g.status === 'invited' && g.invited === 'them' && (
                <>
                  <button type="button" className="sheet-btn primary" disabled={busy} onClick={() => void act(() => acceptGame(id))}>
                    Accept
                  </button>
                  <button type="button" className="fr-btn" disabled={busy} onClick={() => void act(() => declineGame(id))}>
                    Decline
                  </button>
                </>
              )}
              {g.status === 'invited' && g.invited === 'you' && (
                <button type="button" className="fr-btn" disabled={busy} onClick={() => void act(() => resignGame(id))}>
                  Take back the invitation
                </button>
              )}
              {g.status === 'active' && g.you && (
                <>
                  <button
                    type="button"
                    className="fr-btn gm-hint-btn"
                    disabled={!myTurn || busy || g.hint_pending}
                    onClick={() => (advisor && g.players[me].assist ? void askHint(advisor) : setAssistOpen(true))}
                  >
                    <Icon name="auto_awesome" size={15} />
                    Hint
                  </button>
                  {agentStuck && (
                    <button type="button" className="fr-btn" disabled={busy} onClick={() => void act(() => nudgeGameAgent(id))}>
                      <Icon name="refresh" size={15} />
                      Ask again
                    </button>
                  )}
                  {/* LEAVING (owner, 2026-10-19: 當使用者退出棋局的時候對方也要收到通知): you lose, and the other is told. */}
                  {armedResign ? (
                    <span className="fr-btns">
                      <button
                        type="button"
                        className="fr-btn danger"
                        disabled={busy}
                        onClick={() => {
                          setArmedResign(false)
                          void act(() => resignGame(id))
                        }}
                      >
                        Leave and lose?
                      </button>
                      <button type="button" className="fr-btn" onClick={() => setArmedResign(false)}>
                        Keep playing
                      </button>
                    </span>
                  ) : (
                    <button type="button" className="fr-btn" disabled={busy} onClick={() => setArmedResign(true)}>
                      <Icon name="logout" size={15} />
                      Leave game
                    </button>
                  )}
                </>
              )}
              {g.status === 'over' && (
                <button type="button" className="sheet-btn primary" onClick={onClose}>
                  Back to games
                </button>
              )}
            </div>
            {g.you && g.players[them].kind === 'user' && (
              <GameTalk g={g} onSent={(next) => qc.setQueryData<GameView>(key, (old) => newer(old, next))} />
            )}
            {rows.length > 0 && (
              <ol className="gm-moves" aria-label="Moves">
                {rows.map(([n, w, b]) => (
                  <li key={n}>
                    <span className="gm-mn">{n}.</span>
                    <span>{w}</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      )}
      {g && assistOpen && (
        <AssistSheet
          g={g}
          side={me}
          picked={advisor}
          onCancel={() => setAssistOpen(false)}
          onAsk={(a) => void askHint(a)}
        />
      )}
    </div>,
    document.body,
  )
}

function Seat({ g, side }: { g: GameView; side: GameSide }) {
  const p = g.players[side]
  const toMove = g.status === 'active' && g.turn === side
  return (
    <div className={`gm-seat${toMove ? ' to-move' : ''}`}>
      {p.kind === 'agent' ? (
        <span className="gm-agent-face" aria-hidden="true">
          <Icon name="smart_toy" size={18} />
        </span>
      ) : (
        <Avatar picture={p.picture} label={p.name || 'Someone'} className="gm-face" />
      )}
      <span className="gm-name">
        {p.kind === 'user' && p.you ? `${p.name} (you)` : p.name}
        {p.kind === 'agent' && p.yours && <span className="gm-chip">Your agent</span>}
        {/* The owner's rule: the opponent is told. Shown on BOTH boards, from the service's record. */}
        {p.assist && (
          <span className="gm-chip gm-assist" title="Uses AI assist in this game">
            <Icon name="auto_awesome" size={12} />
            AI assist
          </span>
        )}
      </span>
      <span
        className={`gm-color gm-color-${side}${g.type === 'xiangqi' ? ' gm-color-xq' : ''}`}
        aria-label={kindOf(g.type).sides[side]}
      />
    </div>
  )
}

/**
 * Before the FIRST hint: what it costs, said plainly, and which of your agents to ask. Once assist is on, the sheet
 * only asks which agent.
 */
function AssistSheet({
  g,
  side,
  picked,
  onCancel,
  onAsk,
}: {
  g: GameView
  side: GameSide
  picked: GameAgentPick | null
  onCancel: () => void
  onAsk: (a: GameAgentPick) => void
}) {
  const [choice, setChoice] = useState<GameAgentPick | null>(picked)
  const on = g.players[side].assist
  return (
    <Sheet
      title="AI assist"
      onClose={onCancel}
      className="gm-assist-sheet"
      actions={
        <>
          <button type="button" className="sheet-btn" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="sheet-btn primary" disabled={!choice} onClick={() => choice && onAsk(choice)}>
            {on ? 'Ask for a hint' : 'Turn on and ask'}
          </button>
        </>
      }
    >
      <p className="gh-note gm-assist-say">
        {on
          ? 'AI assist is on for this game. Pick the agent to ask.'
          : g.mode === 'match'
            ? 'One of your agents suggests your next move. Your opponent will see “AI assist” beside your name for the rest of this game.'
            : 'One of your agents suggests your next move. “AI assist” shows beside your name for the rest of this game.'}
      </p>
      <AgentPick picked={choice} onPick={setChoice} />
    </Sheet>
  )
}
